import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { AnswerValue } from '../types/types';
import { captureSupabaseError } from '../lib/sentry';

const getUploadErrorMessage = (error: unknown) => {
  const errorMessage =
    typeof error === 'object' && error !== null && 'message' in error
      ? String(error.message).toLowerCase()
      : '';
  const statusCode =
    typeof error === 'object' && error !== null && 'statusCode' in error
      ? String(error.statusCode)
      : '';

  if (
    statusCode === '413' ||
    errorMessage.includes('too large') ||
    errorMessage.includes('entity too large') ||
    errorMessage.includes('payload too large')
  ) {
    return 'This file is too large. Please choose a smaller file and try again.';
  }

  if (
    statusCode === '415' ||
    errorMessage.includes('mime') ||
    errorMessage.includes('content type') ||
    errorMessage.includes('unsupported')
  ) {
    return 'This file type is not supported. Please choose a different file.';
  }

  return 'File upload failed. Please try again.';
};

export const useAssessmentAnswers = (assessmentId?: string) => {
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [loading, setLoading] = useState(false);
  const [savingAnswer, setsavingAnswer] = useState(false);
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});

  const fetchAnswers = useCallback(async () => {
    if (!assessmentId) return;
    setLoading(true);

    const { data, error } = await supabase
      .from('answers')
      .select('question_key, value, question_id')
      .eq('assessment_id', assessmentId);

    if (error) {
      console.error('Error fetching answers:', error);
    } else if (data) {
      // Convert JSONB values to JS
      const formatted = data.reduce(
        (acc, cur) => ({
          ...acc,
          [cur.question_id]: cur.value as AnswerValue,
        }),
        {},
      );
      setAnswers(formatted);
    }

    setLoading(false);
  }, [assessmentId]);

  const uploadFileAndGetPath = async (file: File, questionId: string) => {
    const bucket = 'assessment-answers';
    //const path = `${assessmentId}/${questionKey}/${Date.now()}_${file.name}`;
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');
    const path = `${user.id}/${assessmentId}/${questionId}/${Date.now()}_${
      file.name
    }`;

    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) {
      console.error('Error uploading file:', uploadError);
      throw uploadError;
    }

    return path;
  };

  const saveAnswer = useCallback(
    async (questionKey: string, value: AnswerValue, questionId: string) => {
      if (!assessmentId) return;

      setsavingAnswer(true);
      setSaveErrors((prev) => {
        if (!prev[questionId]) return prev;
        const next = { ...prev };
        delete next[questionId];
        return next;
      });

      let finalValue: AnswerValue = value;

      try {
        // Handle file uploads
        if (value instanceof File) {
          finalValue = await uploadFileAndGetPath(value, questionId);
        }

        const payload = [
          {
            question_key: questionKey,
            question_id: questionId ?? null,
            value: finalValue,
          },
        ];

        const { error } = await supabase.rpc('save_answers', {
          p_assessment_id: assessmentId,
          p_answers: payload,
        });

        if (error) {
          console.error('Error saving answer:', error);
        } else {
          setAnswers((prev) => ({ ...prev, [questionId]: finalValue }));
        }
      } catch (err) {
        captureSupabaseError(err, { op: 'answers.save' });
        console.error('Error calling RPC save_answers:', err);
        setSaveErrors((prev) => ({
          ...prev,
          [questionId]:
            value instanceof File
              ? getUploadErrorMessage(err)
              : 'We could not save this answer. Please try again.',
        }));
      } finally {
        setsavingAnswer(false);
      }
    },
    [assessmentId],
  );

  const completeAssessment = useCallback(async () => {
    if (!assessmentId || !Object.keys(answers).length) {
      return { success: false, error: 'Missing assessment ID or answers.' };
    }

    setsavingAnswer(true);

    try {
      const payload = Object.entries(answers).map(([key, value]) => ({
        question_key: key,
        question_id: key,
        value,
      }));

      const requestBody = {
        assessment_id: assessmentId,
        answers: payload,
      };

      // MUST BE STRING + Content-Type
      const { data, error } = await supabase.functions.invoke(
        'complete-assessment',
        {
          body: requestBody,
        },
      );

      if (error) {
        console.error('Error calling complete-assessment:', error);
        return { success: false, error };
      }

      console.log('Assessment completed:', data);
      return { success: true, data };
    } catch (err) {
      captureSupabaseError(err, { op: 'assessment.complete' });
      console.error('Unexpected error calling complete-assessment:', err);
      return { success: false, error: err };
    } finally {
      setsavingAnswer(false);
    }
  }, [assessmentId, answers]);

  useEffect(() => {
    if (assessmentId) fetchAnswers();
  }, [assessmentId, fetchAnswers]);

  return {
    answers,
    setAnswers,
    saveAnswer,
    saveErrors,
    completeAssessment,
    loading,
    savingAnswer,
  };
};
